import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n";
import { trackStoreClick } from "@/lib/analytics";
import { useStoreLinks } from "@/lib/stores";
import { StoreBadges, GooglePlayLogo, AppleLogo } from "@/components/common/StoreBadges";
import { useLandingConfig } from "./LandingContext";

const APP_STORE_BASE = "https://apps.apple.com/us/app/refound-receipts-returns/id6756928133";

/**
 * The store call-to-action for ad landing pages. Fires the store click
 * conversion event, tagged with `location` so each button can be measured.
 *
 * - variant="badge"  → both official badges (App Store + Google Play). Used in
 *                      hero / final CTA.
 * - variant="button" → compact filled button with the requested CTA wording.
 *                      Points to Google Play on Android devices and to the App
 *                      Store (with the campaign ppid) everywhere else. Used in
 *                      the nav and the sticky mobile bar.
 */
export function AppStoreButton({
  location,
  label,
  variant = "badge",
  className,
}: {
  location: string;
  label: string;
  variant?: "badge" | "button";
  className?: string;
}) {
  const { language } = useLanguage();
  const { appStorePpid } = useLandingConfig();
  const { primaryStore, playStoreUrl } = useStoreLinks(language);
  const appStoreUrl = `${APP_STORE_BASE}?ppid=${appStorePpid}${language === "fr" ? "&l=fr-CA" : ""}`;

  if (variant === "button") {
    const isPlay = primaryStore === "google_play";
    return (
      <a
        href={isPlay ? playStoreUrl : appStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackStoreClick(primaryStore, location, language)}
        aria-label={label}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 font-heading text-base font-semibold text-primary-foreground shadow-sm transition-all duration-200 hover:bg-primary/90 hover:shadow-md active:scale-[0.98]",
          className,
        )}
      >
        {isPlay ? <GooglePlayLogo className="h-5 w-5" /> : <AppleLogo className="h-5 w-5" />}
        {label}
      </a>
    );
  }

  return (
    <StoreBadges
      location={location}
      appStoreUrl={appStoreUrl}
      size="sm"
      className={className}
    />
  );
}
